import {Module} from '@fantasmagames/slot/src/common/Module';
import {SpinButtonEvents} from '@fantasmagames/slot/src/modules/keypad/spinbutton/SpinButtonEvents';
import {AvalancheReelsInitState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheReelsInitState';
import {AvalancheReelsBasicState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheReelsBasicState';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {AvalancheReelsDropOutState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheReelsDropOutState';
import {AvalancheReelsDropNewState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheReelsDropNewState';
import {AvalancheReelsView} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsView';
import {AvalancheReelsEvents} from '@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsEvents';
import {AvalancheAddSymbolsState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheAddSymbolsState';
import {AvalancheRespinState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheRespinState';
import {AvalancheRemoveWinsState} from '@fantasmagames/slot/src/modules/reels/avalanche/states/AvalancheRemoveWinsState';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {WinPresentationEvents} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationEvents';
import {SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {SwipeInteractionEvents} from '@fantasmagames/slot/src/modules/swipeinteraction/SwipeInteractionEvents';
import {BetlevelMenuEvents} from '@fantasmagames/slot/src/modules/popupmenus/betmenu/BetlevelMenuEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {StartScreenEvents} from '@fantasmagames/slot/src/modules/startscreen/StartScreenEvents';

/**
 * avalanche type of reels
 */
export class AvalancheReelsModule extends Module<AvalancheReelsView> {
	public static readonly BASIC_STATE: string = 'AvalancheReelsModule::BASIC_STATE';
	public static readonly INIT_STATE: string = 'AvalancheReelsModule::INIT_STATE';
	public static readonly DROPOUT_STATE: string = 'AvalancheReelsModule::DROPOUT_STATE';
	public static readonly DROPNEW_STATE: string = 'AvalancheReelsModule::DROPNEW_STATE';
	public static readonly RESPIN_STATE: string = 'AvalancheReelsModule::RESPIN_STATE';
	public static readonly ADD_SYMBOLS_STATE: string = 'AvalancheReelsModule::ADD_SYMBOLS_STATE';
	public static readonly REMOVE_WINS_STATE: string = 'AvalancheReelsModule::REMOVE_WINS_STATE';

	protected _isSpinResponseReceived: boolean = false;
	protected _isAvalancheResponseReceived: boolean = false;
	protected _isRespinResponseReceived: boolean = false;
	protected _isAllReelsStopped: boolean = true;

	public init(): void {
		super.init();

		this.setupDefaultReelset();
	}

	public setupDefaultReelset(): void {
		if (!this.logic.hasRestore) {
			this.view.createReelSet(null);
		}
	}

	public restore(): void {
		super.restore();
		this.view.createReelSet(this.logic.getSpinResult().reels);
	}

	protected setupStates(): void {
		this.addState(new AvalancheReelsBasicState(this, AvalancheReelsModule.BASIC_STATE));
		this.addState(new AvalancheReelsInitState(this, AvalancheReelsModule.INIT_STATE));
		this.addState(new AvalancheReelsDropOutState(this, AvalancheReelsModule.DROPOUT_STATE));
		this.addState(new AvalancheReelsDropNewState(this, AvalancheReelsModule.DROPNEW_STATE));
		this.addState(new AvalancheRespinState(this, AvalancheReelsModule.RESPIN_STATE));
		this.addState(new AvalancheAddSymbolsState(this, AvalancheReelsModule.ADD_SYMBOLS_STATE));
		this.addState(new AvalancheRemoveWinsState(this, AvalancheReelsModule.REMOVE_WINS_STATE));
	}

	protected enterFirstState(): void {
		this.changeStateTo(AvalancheReelsModule.INIT_STATE);
	}

	protected setupViews(): void {
		this.setView(Views.REELS_VIEW);
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(SpinButtonEvents.SPIN_BUTTON_PRESSED, this.onSpinButtonPressed);

		this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponseReceived);
		this.on(LogicEvents.AVALANCHE_RESPONSE, this.onAvalancheResponseReveived);
		this.on(LogicEvents.RESPIN_RESPONSE, this.onRespinResponse);
		this.onCurrentStateHandle(LogicEvents.INIT_RESPONSE);

		this.on(FlowModuleEvents.RESPIN_WON, this.onRespinWon);
		this.on(FlowModuleEvents.AVALANCHE_WON, this.onAvalancheWon);

		this.onView(AvalancheReelsView.ALL_REELS_DROPPED_NEW, this.onAllReelsStoppedCallback);
		this.onView(AvalancheReelsView.ALL_REELS_DROPPED_OUT, this.onAllReelsDroppedOutCallback);
		this.onView(AvalancheReelsView.REEL_DROPPED_NEW, this.onReelDroppedNew);
		this.onView(AvalancheReelsView.REEL_DROPPED_OUT, this.onReelDroppedOut);
		this.onView(AvalancheReelsView.FILLING_VOIDS_FINISHED, this.onFillingVoidsFinished);
		this.onView(AvalancheReelsView.ADDING_SYMBOLS_FINISHED, this.onAddingSymbolsFinished);
		this.onView(AvalancheReelsView.REMOVING_SYMBOLS_FINISHED, this.onRemovingSymbolsFinished);

		this.on(WinPresentationEvents.WIN_ANIMATION_FOR_SYMBOL_STARTED, this.onWinAnimationPlayingForSymbol);
		this.on(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, this.onInitialAnimationFinished);
		this.on(WinPresentationEvents.INDIVIDUAL_ANIMATION_FINISHED, this.onIndividualAnimationFinished);

		this.on(SwipeInteractionEvents.DRAGGING_STARTED, this.onDraggingStarted);
		this.on(BetlevelMenuEvents.BET_VALUE_UPDATED, this.onBetValueUpdated);

		this.onCurrentStateHandle(AvalancheReelsEvents.FILLING_VOIDS_FINISHED);
		this.onCurrentStateHandle(WinPresentationEvents.COUNTUP_FINISHED);
		this.onCurrentStateHandle(FlowModuleEvents.NO_WIN);
		this.onCurrentStateHandle(AvalancheReelsEvents.REMOVING_WIN_FINISHED);
		this.onCurrentStateHandle(AvalancheReelsEvents.ADDING_NEW_FINISHED);
		this.onCurrentStateHandle(FlowModuleEvents.CAN_DROP_NEW);

		this.onViewEmitEvent(AvalancheReelsView.REELS_FADING_OUT_FINISHED, AvalancheReelsEvents.REELS_FADING_OUT_FINISHED);
		this.onViewEmitEvent(AvalancheReelsView.REELS_FADING_OUT_STARTED, AvalancheReelsEvents.REELS_FADING_OUT_STARTED);
		this.onViewEmitEvent(AvalancheReelsView.REELS_FADING_IN_FINISHED, AvalancheReelsEvents.REELS_FADING_IN_FINISHED);
		this.onViewEmitEvent(AvalancheReelsView.REELS_FADING_IN_STARTED, AvalancheReelsEvents.REELS_FADING_IN_STARTED);

		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsIntroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_RESTORE_INTRO_STARTED, this.onFreeSpinsIntroRestoreStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_FINISHED, this.onFreeSpinsIntroFinished);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_STARTED, this.onFreeSpinsOutroStarted);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsOutroFinished);

		this.on(StartScreenEvents.INTRO_STARTED, this.onStartScreenIntroStarted);
		this.on(StartScreenEvents.EXIT_FINISHED, this.onStartScreenIntroFinished);
	}

	protected onStartScreenIntroStarted(): void {
		this.view.hide();
	}

	protected onStartScreenIntroFinished(): void {
		this.view.show();
	}

	protected onFreeSpinsIntroRestoreStarted(): void {
		this.view.hide();
	}

	protected onFreeSpinsIntroStarted(): void {
		this.view.fadeOutSymbols();
	}

	protected onFreeSpinsIntroFinished(): void {
		this.view.show();
		this.view.fadeInSymbols();
	}

	protected onFreeSpinsOutroStarted(): void {
		this.view.fadeOutSymbols();
	}

	protected onFreeSpinsOutroFinished(): void {
		this.view.fadeInSymbols();
	}

	protected onRespinWon(): void {
		this.gotoState(AvalancheReelsModule.RESPIN_STATE);
	}

	protected onAvalancheWon(): void {
		this.isAvalancheResponseReceived = false;
		this.gotoState(AvalancheReelsModule.REMOVE_WINS_STATE);
	}

	protected onWinAnimationPlayingForSymbol(eventId: string, pos: SymbolPosition): void {
	}

	protected onIndividualAnimationFinished(): void {
		this.view.showAllSymbols();
	}

	protected onInitialAnimationFinished(): void {
		this.view.showAllSymbols();
	}

	protected onDraggingStarted(): void {
		this.view.showAllSymbols();
	}

	protected onBetValueUpdated(): void {
		this.view.showAllSymbols();
	}

	protected onSpinButtonPressed(): void {
		this.resetParameters();
		this.logic.resetRound();
		this.currentState.handle(SpinButtonEvents.SPIN_BUTTON_PRESSED);
	}

	protected onAllReelsStoppedCallback(eventID: string, reelNr: number): void {
		this.currentState.handle(AvalancheReelsView.ALL_REELS_DROPPED_NEW);

		this.eventManager.emit(AvalancheReelsEvents.REEL_DROP_NEW_FINISHED, reelNr);
		this.eventManager.emit(ReelsEvents.ALL_REELS_STOPPED);
		this.eventManager.emit(ReelsEvents.AVALANCHE_ENDED);
		this.eventManager.emit(AvalancheReelsEvents.ALL_REELS_DROP_NEW_FINISHED);
	}

	protected onAllReelsDroppedOutCallback(eventID: string, reelNr: number): void {
		this.currentState.handle(AvalancheReelsView.ALL_REELS_DROPPED_OUT);
		this.eventManager.emit(AvalancheReelsEvents.REEL_DROP_OUT_FINISHED, reelNr);
		this.eventManager.emit(AvalancheReelsEvents.ALL_REELS_DROP_OUT_FINISHED);
	}

	protected onReelDroppedNew(eventID: string, reelNr: number): void {
		this.eventManager.emit(AvalancheReelsEvents.REEL_DROP_NEW_FINISHED, reelNr);
	}

	protected onReelDroppedOut(eventID: string, reelNr: number): void {
		this.eventManager.emit(AvalancheReelsEvents.REEL_DROP_OUT_FINISHED, reelNr);
	}

	protected onFillingVoidsFinished(eventID: string, reelNr: number): void {
		this.eventManager.emit(AvalancheReelsEvents.FILLING_VOIDS_FINISHED);
	}

	protected onAddingSymbolsFinished(eventID: string, reelNr: number): void {
		this.eventManager.emit(AvalancheReelsEvents.ADDING_NEW_FINISHED);
		// this.currentState.handle(AvalancheReelsEvents.ADDING_NEW_FINISHED);

		this._isRespinResponseReceived = false;
	}

	protected onRemovingSymbolsFinished(eventID: string, symbols: SymbolPosition[]): void {
		this.eventManager.emit(AvalancheReelsEvents.REMOVING_WIN_FINISHED);
	}

	protected onSpinResponseReceived(): void {
		this._isSpinResponseReceived = true;
		this.currentState.handle(LogicEvents.SPIN_RESPONSE);
	}

	protected onAvalancheResponseReveived(): void {
		this.isAvalancheResponseReceived = true;
		this.currentState.handle(LogicEvents.AVALANCHE_RESPONSE);
	}

	protected onRespinResponse(): void {
		this._isRespinResponseReceived = true;
		this.currentState.handle(LogicEvents.RESPIN_RESPONSE);
	}

	protected resetParameters(): void {
		this._isSpinResponseReceived = false;
		this._isAllReelsStopped = false;
		this.isAvalancheResponseReceived = false;
		this._isRespinResponseReceived = false;
	}

	public get isSpinResponseReceived(): boolean {
		return this._isSpinResponseReceived;
	}

	public get isAvalancheResponseReceived(): boolean {
		return this._isAvalancheResponseReceived;
	}

	public set isAvalancheResponseReceived(v: boolean) {
		this._isAvalancheResponseReceived = v;
	}

	public get isRespinResponseReceived(): boolean {
		return this._isRespinResponseReceived;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/reels/avalanche/AvalancheReelsModule.ts